import { useState, useEffect, useMemo } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import Button from "./components/buttonWrapper/Button.jsx";
import Header from "./components/Header/Header.jsx";
import Footer from "./components/Footer/Footer.jsx";
import Home from "./pages/Home/Home.jsx";
import Menu from "./pages/Menu/Menu.jsx";
import Team from "./pages/Team/Team.jsx";
import Admin from "./pages/Admin/Admin.jsx";
import { Analytics } from "@vercel/analytics/react";
import { AnimatePresence, motion } from "framer-motion";
import "./App.css";
import carpeDiemLogo from "./assets/images/logo/Logo_Carpe_Diem.png";
import Login from "./pages/Login/Login.jsx";
import Events from "./pages/Events/Events.jsx";
import Tourism from "./pages/Tourism/Tourism.jsx";
import Allergens from "./pages/Allergens/Allergens.jsx";
import LegalNotice from "./pages/Legalnotice/Legalnotice.jsx";
import Contact from "./pages/Contact/Contact.jsx";
import { isAdminAuthenticated } from "./utils/adminAuth";
import {
  fetchImagesConfigFromApi,
  fetchMenusFromApi,
} from "./utils/contentApi";

function ProtectedAdminRoute({ children }) {
  const location = useLocation();
  const [authStatus, setAuthStatus] = useState("checking");
  const redirectState = useMemo(() => {
    const from = location.pathname.startsWith("/admin")
      ? location.pathname
      : "/admin";

    return { from };
  }, [location.pathname]);

  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      setAuthStatus("checking");
      const isAuthenticated = await isAdminAuthenticated();
      if (!isMounted) {
        return;
      }

      setAuthStatus(isAuthenticated ? "authenticated" : "unauthenticated");
    };

    checkAuth();

    return () => {
      isMounted = false;
    };
  }, [location.pathname]);

  if (authStatus === "checking") {
    return null;
  }

  if (authStatus === "unauthenticated") {
    // Prevent redirect loops while route transition animations still render old admin route.
    if (location.pathname === "/loginMagVinc") {
      return null;
    }

    return <Navigate to="/loginMagVinc" replace state={redirectState} />;
  }

  return children;
}

function AnimatedRoutes() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0 });
      return;
    }

    const targetId = decodeURIComponent(location.hash.slice(1));
    const scrollToAnchor = () => {
      const target = document.getElementById(targetId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    // Wait one frame so routed content is mounted before anchor lookup.
    requestAnimationFrame(scrollToAnchor);
  }, [location.pathname, location.hash]);

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Home />
            </motion.div>
          }
        />
        <Route
          path="/menu"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Menu />
            </motion.div>
          }
        />
        <Route
          path="/restaurant-chambres"
          element={<Navigate to="/notre-restaurant" replace />}
        />
        <Route
          path="/notre-restaurant"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Team section="restaurant" />
            </motion.div>
          }
        />
        <Route
          path="/nos-chambres"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Team section="chambres" />
            </motion.div>
          }
        />
        <Route
          path="/reservation"
          element={<Navigate to="/notre-restaurant#reservation-restaurant" replace />}
        />
        <Route
          path="/reservation-restaurant"
          element={<Navigate to="/notre-restaurant#reservation-restaurant" replace />}
        />
        <Route
          path="/reservation-chambre"
          element={<Navigate to="/nos-chambres#reservation-chambre" replace />}
        />
        <Route
          path="/evenements"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Events />
            </motion.div>
          }
        />
        <Route
          path="/tourisme"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Tourism />
            </motion.div>
          }
        />
        <Route
          path="/allergenes"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Allergens />
            </motion.div>
          }
        />
        <Route
          path="/legalnotices"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <LegalNotice />
            </motion.div>
          }
        />
        <Route
          path="/loginMagVinc"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Login />
            </motion.div>
          }
        />
        <Route
          path="/contact"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Contact />
            </motion.div>
          }
        />
        <Route
          path="/admin/*"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <ProtectedAdminRoute>
                <Admin />
              </ProtectedAdminRoute>
            </motion.div>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

function AppContent() {
  const [showButton, setShowButton] = useState(false);
  const [, setContentSyncVersion] = useState(0);
  const [showIntro, setShowIntro] = useState(true);
  const location = useLocation();

  // Vérifier si on est dans une page admin
  const isAdminPage = location.pathname.startsWith("/admin");

  useEffect(() => {
    const handleScroll = () => {
      setShowButton(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!showIntro) {
      return undefined;
    }

    const introTimer = window.setTimeout(() => {
      setShowIntro(false);
    }, 1700);

    return () => {
      window.clearTimeout(introTimer);
    };
  }, [showIntro]);

  useEffect(() => {
    let isMounted = true;

    const syncContentCache = async () => {
      let hasChanged = false;

      try {
        const menus = await fetchMenusFromApi();
        const serializedMenus = JSON.stringify(menus);
        if (
          Array.isArray(menus) &&
          menus.length > 0 &&
          localStorage.getItem("carpeDiemMenus") !== serializedMenus
        ) {
          localStorage.setItem("carpeDiemMenus", serializedMenus);
          hasChanged = true;
        }
      } catch (_error) {
        // Keep existing local cache if API is temporarily unavailable.
      }

      try {
        const imagesConfig = await fetchImagesConfigFromApi();
        const serializedImagesConfig = JSON.stringify(imagesConfig);
        if (
          imagesConfig &&
          Object.keys(imagesConfig).length > 0 &&
          localStorage.getItem("carpeDiemImagesConfig") !==
            serializedImagesConfig
        ) {
          localStorage.setItem("carpeDiemImagesConfig", serializedImagesConfig);
          hasChanged = true;
        }
      } catch (_error) {
        // Keep existing local cache if API is temporarily unavailable.
      }

      if (hasChanged && isMounted) {
        setContentSyncVersion((prev) => prev + 1);
        window.dispatchEvent(new Event("carpe-content-changed"));
      }
    };

    syncContentCache();
    const onFocus = () => {
      syncContentCache();
    };
    const intervalId = window.setInterval(syncContentCache, 60_000);

    window.addEventListener("focus", onFocus);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  return (
    <>
      <AnimatePresence>
        {showIntro && !isAdminPage ? (
          <motion.div
            className="carpe-loader"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.38, ease: "easeInOut" }}
            aria-hidden="true"
          >
            <motion.img
              src={carpeDiemLogo}
              alt="Carpe Diem"
              className="carpe-loader-logo"
              initial={{ opacity: 0, y: 28, scale: 0.88, rotate: -1.5 }}
              animate={{
                opacity: [0, 1, 1, 0.92],
                y: [28, 0, -2, 0],
                scale: [0.88, 1.03, 1, 1],
                rotate: [-1.5, 0, 0, 0],
              }}
              transition={{
                duration: 1.28,
                times: [0, 0.35, 0.75, 1],
                ease: "easeOut",
              }}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>

      <div
        className={
          showIntro && !isAdminPage ? "app-shell app-shell-hidden" : "app-shell"
        }
      >
        {!isAdminPage && <Button visible={showButton} />}
        {location.pathname !== "/legalnotices" && !isAdminPage && <Header />}
        <AnimatedRoutes />
        {!isAdminPage && <Footer />}
      </div>
      <Analytics />
    </>
  );
}

const App = () => (
  <Router>
    <AppContent />
  </Router>
);

export default App;
